import insightsClient from 'search-insights'
import { hashString, uuid4 } from '~/utils/cryptoHelpers'

declare global {
  interface Window {
    dataLayer: any
  }
}

export const useAlgoliaSearchInsights = () => {
  const ALGOLIA_USER_TOKEN_KEY = 'konfetti-algolia-tracking-user-token'
  const token = ref<string | undefined>(undefined)
  const hasBeenInitialized = ref(false)

  /**
   * Variables
   * */
  const $config = useRuntimeConfig()

  /**
   * Methods
   * */
  const fnSetUserToken = () => {
    if (process.server) {
      return
    }

    // Try to get existing token from localStorage
    token.value = window?.localStorage?.getItem(
      ALGOLIA_USER_TOKEN_KEY
    ) as string

    // If no token exists or it's invalid, generate a new one
    if (!token.value || token.value === 'undefined') {
      token.value = uuid4()
    }

    // If user is logged in, use hashed email as token instead
    const storageUser = window?.localStorage?.getItem('user')
    if (storageUser) {
      try {
        const user = JSON.parse(storageUser)
        if (user?.data?.email) {
          token.value = hashString(user.data.email)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error parsing user from localStorage:', error)
      }
    }

    // Save token to localStorage
    if (window?.localStorage) {
      window.localStorage.setItem(ALGOLIA_USER_TOKEN_KEY, token.value)
    }

    // Set the token in Algolia Insights
    insightsClient('setUserToken', token.value)
  }

  const fnInit = () => {
    if (!process.server) {
      // Only initialize Algolia client in production
      if ($config.public.appEnv === 'production') {
        insightsClient('init', {
          appId: $config.public.algoliaAppId,
          apiKey: $config.public.algoliaApiClientKey,
        })

        insightsClient(
          'onUserTokenChange',
          (userToken) => {
            if (!window?.dataLayer) {
              return
            }

            window.dataLayer.push({
              algoliaUserToken: userToken,
            })
          },
          { immediate: true }
        )
      }

      hasBeenInitialized.value = true
    }

    // Always set user token, even in development (for consistency)
    fnSetUserToken()
  }

  const mockedInsightsClient = (method: string, payload: any) => {
    // eslint-disable-next-line no-console
    console.log('Mocked Algolia Insights Client triggered.', method, payload)
  }

  const wrappedClient = (...args) => {
    if (!hasBeenInitialized.value) fnInit()
    return insightsClient(...args)
  }

  const wrappedMockedClient = (...args) => {
    if (!hasBeenInitialized.value) fnInit()
    return mockedInsightsClient(...args)
  }

  const client =
    $config.public.appEnv === 'production' ? wrappedClient : wrappedMockedClient

  // Initialize token immediately when composable is called
  if (!hasBeenInitialized.value && !process.server) {
    fnInit()
  }

  return {
    client,

    token: computed(() => token.value),

    /* Methods */
    fnInit,
    fnSetUserToken,
  }
}
